// Authoritative client-side instructor resolver.
// ALL "who teaches this class" questions across the app MUST go through this
// module. It calls the Postgres function public.resolve_class_instructor which
// reads instructor_class_assignments and applies the precedence rules:
//   (1) exact session match, (2) date-range match, (3) temporary coverage,
//   (4) active with open dates, (5) upcoming/future, (6) historical.
//
// Never resolve instructor names from src/data/classes.ts strings,
// src/lib/classInstructors.ts (which is now a fallback-only map), or
// free-form Executive Memory. Those cannot represent temporary coverage,
// date-scoped assignments, or replacements.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ResolvedInstructor {
  assignment_id: string | null;
  instructor_id: string | null;
  full_name: string | null;
  preferred_name: string | null;
  display_name: string | null;
  status: string | null;
  assignment_kind: string | null;
  temporary_until_replaced: boolean | null;
  session_label: string | null;
  start_date: string | null;
  end_date: string | null;
  match_reason: string | null;
  is_placeholder: boolean;
  note: string | null;
}

function fmtDate(d?: Date | string | null): string | undefined {
  if (!d) return undefined;
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
}

const UNASSIGNED: ResolvedInstructor = {
  assignment_id: null, instructor_id: null, full_name: null,
  preferred_name: null, display_name: null, status: null,
  assignment_kind: null, temporary_until_replaced: null,
  session_label: null, start_date: null, end_date: null,
  match_reason: "No instructor assigned", is_placeholder: true,
  note: "Instructor not yet assigned.",
};

/**
 * Public (signed-out) fallback.
 *
 * `resolve_class_instructor` is an internal SECURITY DEFINER routine and is no
 * longer executable by anonymous visitors. Parents still need to see who
 * teaches a class, so the public site reads `public_class_instructors` — a
 * view that exposes only the display name of a live/upcoming assignment on a
 * class that is already publicly visible. No contact details, no history.
 */
async function resolvePublicClassInstructor(
  classSlug: string,
  sessionLabel?: string | null,
): Promise<ResolvedInstructor> {
  let query = (supabase as any)
    .from("public_class_instructors")
    .select("class_slug,session_label,status,temporary_until_replaced,display_name")
    .eq("class_slug", classSlug);
  if (sessionLabel) query = query.eq("session_label", sessionLabel);
  const { data } = await query.limit(1);
  const row = Array.isArray(data) ? data[0] : null;
  if (!row?.display_name) return UNASSIGNED;
  return {
    ...UNASSIGNED,
    full_name: row.display_name,
    display_name: row.display_name,
    status: row.status ?? null,
    temporary_until_replaced: row.temporary_until_replaced ?? null,
    session_label: row.session_label ?? null,
    match_reason: row.status === "temporary" ? "Temporary coverage" : "Current assignment",
    is_placeholder: false,
    note: null,
  };
}

export async function resolveClassInstructor(
  classSlug: string,
  atDate?: Date | string | null,
  sessionLabel?: string | null,
): Promise<ResolvedInstructor> {
  const { data, error } = await supabase.rpc("resolve_class_instructor", {
    _class_slug: classSlug,
    _at_date: fmtDate(atDate) ?? fmtDate(new Date())!,
    _session_label: sessionLabel ?? null,
  });
  if (error) {
    // Signed-out visitor: the internal resolver is admin/authenticated-only.
    return await resolvePublicClassInstructor(classSlug, sessionLabel);
  }
  if (!data || !data.length) return UNASSIGNED;
  return data[0] as ResolvedInstructor;
}

// Batch helper — resolve a list of (classSlug, date, sessionLabel) at once
// with a single round-trip per key. Callers should dedupe on the client.
export async function resolveManyClassInstructors(
  keys: Array<{ classSlug: string; date?: Date | string | null; sessionLabel?: string | null }>,
): Promise<Map<string, ResolvedInstructor>> {
  const out = new Map<string, ResolvedInstructor>();
  await Promise.all(
    keys.map(async (k) => {
      const key = `${k.classSlug}|${fmtDate(k.date) ?? ""}|${k.sessionLabel ?? ""}`;
      out.set(key, await resolveClassInstructor(k.classSlug, k.date, k.sessionLabel));
    }),
  );
  return out;
}

// React hook — resolves once on mount and re-resolves if the resolver key changes.
export function useResolvedInstructor(
  classSlug: string | null | undefined,
  atDate?: Date | string | null,
  sessionLabel?: string | null,
): { data: ResolvedInstructor | null; loading: boolean } {
  const [data, setData] = useState<ResolvedInstructor | null>(null);
  const [loading, setLoading] = useState(true);
  const dateKey = fmtDate(atDate) ?? "";
  useEffect(() => {
    let cancelled = false;
    if (!classSlug) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    resolveClassInstructor(classSlug, atDate, sessionLabel).then((r) => {
      if (!cancelled) {
        setData(r);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classSlug, dateKey, sessionLabel]);
  return { data, loading };
}

export function formatInstructorForDisplay(r: ResolvedInstructor | null): string {
  if (!r || r.is_placeholder || !r.display_name) return "Instructor not yet assigned";
  if (r.status === "temporary") {
    if (r.temporary_until_replaced) return `${r.display_name} (temporary coverage)`;
    if (r.end_date) return `${r.display_name} (temp through ${r.end_date})`;
    return `${r.display_name} (temporary)`;
  }
  if (r.status === "upcoming" || r.status === "future") {
    return `${r.display_name} (starting ${r.start_date ?? "TBD"})`;
  }
  return r.display_name;
}
